import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { ClientActivityEvent, ClientActivityKind } from '@/lib/clients/activity-timeline';
import { formatDateLocale } from '@/lib/utils';
import { Activity, ClipboardList, FileText, PersonStanding } from 'lucide-react';
import { cn } from '@/lib/utils';

const KIND_STYLES: Record<
  ClientActivityKind,
  { dot: string; icon: typeof Activity }
> = {
  assessment: { dot: 'bg-emerald-500', icon: ClipboardList },
  posture: { dot: 'bg-primary', icon: PersonStanding },
  program: { dot: 'bg-violet-500', icon: Activity },
  note: { dot: 'bg-amber-500', icon: FileText },
};

interface ClientActivityTimelineProps {
  events: ClientActivityEvent[];
  loading?: boolean;
}

export function ClientActivityTimeline({ events, loading }: ClientActivityTimelineProps) {
  return (
    <Card className="border-none shadow-md" data-testid="client-activity-timeline">
      <CardHeader>
        <CardTitle>Activity timeline</CardTitle>
        <CardDescription>
          Assessments, posture analyses, programs, and notes — newest first
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading activity…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No activity recorded yet.</p>
        ) : (
          <div className="relative pl-8 border-l-2 border-muted space-y-5 py-2 ml-2">
            {events.map((event) => {
              const style = KIND_STYLES[event.kind];
              const Icon = style.icon;
              return (
                <div key={`${event.kind}-${event.id}`} className="relative">
                  <div
                    className={cn(
                      'absolute -left-[41px] top-0 h-5 w-5 rounded-full border-4 border-background flex items-center justify-center',
                      style.dot
                    )}
                  >
                    <Icon className="h-2.5 w-2.5 text-white" aria-hidden />
                  </div>

                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                    <div className="min-w-0">
                      <h4 className="text-sm font-semibold">{event.title}</h4>
                      {event.summary && (
                        <p className="text-sm text-muted-foreground mt-1 break-words">
                          {event.summary}
                        </p>
                      )}
                      {event.href && (
                        <Button variant="link" className="p-0 h-auto text-primary mt-1" asChild>
                          <Link to={event.href}>View details</Link>
                        </Button>
                      )}
                    </div>
                    <time
                      className="text-xs text-muted-foreground whitespace-nowrap shrink-0"
                      dateTime={
                        event.occurredAt instanceof Date && !isNaN(event.occurredAt.getTime())
                          ? event.occurredAt.toISOString()
                          : undefined
                      }
                    >
                      {formatDateLocale(event.occurredAt, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </time>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
